import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { PresetDatePicker } from '@plato/app-shared/components/preset-date-picker';
import { CancellableSearchBox } from '@plato/ui-legacy/components/search/cancellable-search-box';
import { useTranslation } from 'react-i18next';
import { VisibilityWindow } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useTeamFilters } from '../hooks/use-team-filters.ts';
import { RoleSelect } from './role-select.tsx';
import { SalesRegionSelect } from './sales-region-select.tsx';

export function TeamFilters() {
    const { appliedFilters, setAppliedFilters } = useTeamFilters();
    const { canFilterCustomerBySalesRegion } = useAppConfig();
    const { t } = useTranslation('team');
    const sendEvent = useAnalytics();

    const trackFilter = (filter: 'search' | 'date' | 'sales_region' | 'role'): void => {
        sendEvent('team_page_filter_used', { filter });
    };

    const searchValue = appliedFilters.searchUsers ?? '';

    const { data: filterOptions, isLoading: isFilterOptionsLoading } =
        api.platoUser.query.notes.getStatsFilterOptions.useQuery();

    return (
        <div className="flex">
            <div className="flex w-full flex-row gap-2 flex-wrap">
                <div className="flex w-full grow md:w-auto md:flex-none">
                    <CancellableSearchBox
                        value={searchValue}
                        onChange={(value) => {
                            if (value && value !== appliedFilters.searchUsers) {
                                trackFilter('search');
                            }
                            setAppliedFilters((filters) => ({ ...filters, searchUsers: value || undefined }));
                        }}
                        placeholder={t('search_placeholder_users')}
                        aria-label={t('search_placeholder_users')}
                        className="w-full"
                    />
                </div>

                <div className="flex flex-1 gap-2 overflow-auto scrollbar-none">
                    <PresetDatePicker
                        value={{
                            preset: appliedFilters.window,
                            dateRange: appliedFilters.customDateRange ?? null,
                        }}
                        onChange={({ preset, dateRange: customRange }) => {
                            trackFilter('date');
                            setAppliedFilters((filters) =>
                                preset === VisibilityWindow.CUSTOM_DATES && customRange
                                    ? { ...filters, window: preset, customDateRange: customRange }
                                    : { ...filters, window: preset },
                            );
                        }}
                    />

                    {canFilterCustomerBySalesRegion && (
                        <SalesRegionSelect
                            isLoading={isFilterOptionsLoading}
                            value={appliedFilters.salesRegion}
                            onChange={(salesRegion) => {
                                trackFilter('sales_region');
                                setAppliedFilters((filters) => ({ ...filters, salesRegion }));
                            }}
                            options={filterOptions?.salesRegion ?? []}
                        />
                    )}

                    <RoleSelect
                        isLoading={isFilterOptionsLoading}
                        value={appliedFilters.userRole}
                        onChange={(userRole) => {
                            trackFilter('role');
                            setAppliedFilters((filters) => ({ ...filters, userRole }));
                        }}
                        options={filterOptions?.userRole ?? []}
                    />
                </div>
            </div>
        </div>
    );
}
